import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, TrendingDown, CalendarClock, Receipt, Target, Pencil, BarChart3, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Legend } from 'recharts';
import { toast } from 'sonner';
import { Skeleton } from '../../ui/skeleton';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { easerGet, easerPut, errorMessage } from '../../../lib/easerApi';
import { fmtEur, fmtEurCents } from './crmShared';

const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

export function useChartColors() {
  return useMemo(() => {
    const read = (name, fallback) => {
      if (typeof window === 'undefined') return fallback;
      const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      return v ? `hsl(${v})` : fallback;
    };
    return { primary: read('--primary', 'hsl(160 33% 27%)'), accent: read('--accent', 'hsl(182 75% 42%)'), muted: read('--muted-foreground', 'hsl(215 16% 47%)'), apricot: read('--brand-apricot', 'hsl(28 85% 60%)'), border: read('--border', 'hsl(214 32% 91%)') };
  }, []);
}

/** Barres CA HT mois par mois : année en cours vs année précédente (+ ligne d'objectif pour l'agence). */
function MonthlyChart({ socid, onUnauthorized, year }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const colors = useChartColors();
  useEffect(() => {
    let cancelled = false;
    easerGet('/api/easer/crm/revenue/monthly', socid ? { socid } : {}).then((r) => { if (!cancelled) setData(r.data); }).catch((err) => {
      if (err?.response?.status === 401) return onUnauthorized();
      if (!cancelled) setError(errorMessage(err, 'Graphique indisponible'));
    });
    return () => { cancelled = true; };
  }, [socid, onUnauthorized]);
  if (error) return <p className="text-xs text-destructive mt-2">{error}</p>;
  if (!data) return <Skeleton className="h-44 rounded-lg mt-2" />;
  const items = data.items || [];
  const target = items.find((i) => i.target_ht)?.target_ht || 0;
  const y = data.year || year;
  return (
    <div className="mt-2" data-testid="crm-revenue-chart">
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={items} margin={{ top: 8, right: 4, left: -18, bottom: 0 }} barGap={2} barCategoryGap="22%">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.border} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: colors.muted }} axisLine={false} tickLine={false} interval={0} />
            <YAxis tick={{ fontSize: 10, fill: colors.muted }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
            <Tooltip formatter={(v, name) => [fmtEur(v), name]} labelFormatter={(l) => `${l}`} contentStyle={{ borderRadius: 10, fontSize: 12 }} cursor={{ fill: colors.border, opacity: 0.4 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
            <Bar dataKey="previous_ht" name={`${y - 1}`} fill={colors.muted} opacity={0.45} radius={[3, 3, 0, 0]} />
            <Bar dataKey="current_ht" name={`${y}`} fill={colors.primary} radius={[3, 3, 0, 0]} />
            {target > 0 && <ReferenceLine y={target} stroke={colors.apricot} strokeDasharray="4 3" label={{ value: 'objectif', position: 'insideTopRight', fontSize: 10, fill: colors.apricot }} />}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">Cumul {y} : <span className="font-medium text-foreground">{fmtEur(data.totals?.current_ht)}</span> HT · {y - 1} : {fmtEur(data.totals?.previous_ht)} HT</p>
    </div>
  );
}

/** Barre de progression vers l'objectif mensuel : facturé (plein) + à venir (hachuré) ; modification de l'objectif par le manager. */
function TargetProgress({ target, invoicedHt, forecastHt, month, year, onSaved, onUnauthorized }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [scope, setScope] = useState('default');
  const [saving, setSaving] = useState(false);
  const goal = target?.monthly_ht || 0;
  const invoicedPct = goal ? Math.min(100, (invoicedHt / goal) * 100) : 0;
  const forecastPct = goal ? Math.min(100 - invoicedPct, (forecastHt / goal) * 100) : 0;
  const projectedPct = target?.projected_pct;

  const save = async () => {
    const n = Number(String(value).replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) return toast.error('Saisissez un montant HT valide');
    setSaving(true);
    try {
      const ym = `${year}-${String(month).padStart(2, '0')}`;
      await easerPut('/api/easer/crm/objectif', scope === 'month' ? { monthly_ht: n, month: ym } : { monthly_ht: n });
      toast.success(scope === 'month' ? `Objectif de ${MONTHS[month - 1]} enregistré` : 'Objectif mensuel enregistré');
      setOpen(false); onSaved();
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      toast.error(errorMessage(err, 'Enregistrement impossible'));
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-border/70" data-testid="crm-revenue-target">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1"><Target className="h-3.5 w-3.5" /> Objectif du mois{target?.is_override ? ' (spécifique)' : ''}</p>
        <button type="button" onClick={() => { setValue(goal ? String(goal) : ''); setScope(target?.is_override ? 'month' : 'default'); setOpen(true); }} className="h-8 px-2 rounded-md text-xs font-medium text-[hsl(var(--primary))] inline-flex items-center gap-1 hover:bg-secondary" data-testid="crm-target-edit">
          <Pencil className="h-3.5 w-3.5" /> {goal ? fmtEur(goal) + ' HT' : 'Définir'}
        </button>
      </div>
      {goal > 0 ? (
        <>
          <div className="mt-2 h-3 rounded-full bg-secondary overflow-hidden flex" role="progressbar" aria-valuenow={Math.round(projectedPct || 0)} aria-valuemin={0} aria-valuemax={100} aria-label="Progression vers l'objectif">
            <div className="h-full bg-[hsl(var(--primary))]" style={{ width: `${invoicedPct}%` }} />
            <div className="h-full bg-[hsl(var(--primary))]/35 bg-[repeating-linear-gradient(135deg,transparent,transparent_3px,hsl(var(--primary)/0.5)_3px,hsl(var(--primary)/0.5)_6px)]" style={{ width: `${forecastPct}%` }} />
          </div>
          <div className="mt-1 flex items-center justify-between text-xs">
            <span><span className="font-semibold" data-testid="crm-target-invoiced-pct">{Math.round(target.invoiced_pct || 0)} %</span> facturé <span className="text-muted-foreground">· {Math.round(projectedPct || 0)} % projeté</span></span>
            <span className={`${(projectedPct || 0) >= 100 ? 'text-[hsl(var(--primary))] font-medium' : 'text-muted-foreground'}`} data-testid="crm-target-remaining">{(projectedPct || 0) >= 100 ? 'Objectif atteint avec le planning' : `reste ${fmtEur(target.remaining_ht)} · ${target.days_left} j`}</span>
          </div>
        </>
      ) : <p className="text-xs text-muted-foreground mt-1">Aucun objectif défini : touchez « Définir » pour fixer le CA HT mensuel visé par l'agence.</p>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm w-[92vw] rounded-2xl" data-testid="crm-target-dialog">
          <DialogHeader className="text-left"><DialogTitle className="font-['Space_Grotesk']">Objectif de chiffre d'affaires</DialogTitle><DialogDescription>Montant HT visé par mois, partagé par tous les managers et visible dans le back-office.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label htmlFor="crm-target-input">Objectif mensuel HT (€)</Label><Input id="crm-target-input" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder="ex. 15000" className="h-12 text-lg" data-testid="crm-target-input" /></div>
            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Portée de l'objectif">
              {[['default', 'Tous les mois'], ['month', `${MONTHS[month - 1]} seulement`]].map(([id, lbl]) => (
                <button key={id} type="button" role="radio" aria-checked={scope === id} onClick={() => setScope(id)} data-testid={`crm-target-scope-${id}`}
                  className={`h-11 rounded-lg border text-sm font-medium capitalize ${scope === id ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]' : 'border-border text-muted-foreground'}`}>{lbl}</button>
              ))}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0"><Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Annuler</Button><Button onClick={save} disabled={saving} className="bg-[hsl(var(--primary))] text-white" data-testid="crm-target-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Chiffre d'affaires : mois ou année en cours face à l'an dernier (à date égale + période complète).
 * En vue « Mois », ajoute l'estimation du CA à venir calculée sur les bons d'intervention validés non facturés,
 * l'objectif mensuel de l'agence (agence seulement) et un graphique mois par mois N / N-1.
 * `socid` absent = agence entière ; présent = profil client.
 */
export function RevenueWidget({ socid = null, onUnauthorized, compact = false, testId = 'crm-revenue' }) {
  const [period, setPeriod] = useState('month');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [showChart, setShowChart] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setData(null); setError('');
    const url = socid ? `/api/easer/crm/clients/${socid}/revenue` : '/api/easer/crm/revenue';
    easerGet(url, { period }).then((r) => { if (!cancelled) setData(r.data); }).catch((err) => {
      if (err?.response?.status === 401) return onUnauthorized();
      if (!cancelled) setError(errorMessage(err, 'CA indisponible'));
    });
    return () => { cancelled = true; };
  }, [socid, period, onUnauthorized, tick]);

  const now = new Date();
  const month = data?.month || now.getMonth() + 1;
  const year = data?.year || now.getFullYear();
  const cur = data?.current;
  const prev = data?.previous_to_date;
  const prevFull = data?.previous_full;
  const hasPrev = prev?.has_data || prevFull?.has_data;
  const label = period === 'month' ? `${MONTHS[month - 1]} ${year}` : `année ${year}`;
  const prevLabel = period === 'month' ? `${MONTHS[month - 1]} ${year - 1}` : `année ${year - 1}`;
  const delta = data?.delta_pct_ht;
  const fc = data?.forecast;
  const fmt = compact ? fmtEur : fmtEurCents;

  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> Chiffre d'affaires{socid ? '' : ' agence'}</p>
        <div className="inline-flex rounded-lg border border-border p-0.5" role="radiogroup" aria-label="Période du chiffre d'affaires">
          {[['month', 'Mois'], ['year', 'Année']].map(([id, lbl]) => (
            <button key={id} type="button" role="radio" aria-checked={period === id} onClick={() => setPeriod(id)} data-testid={`${testId}-${id}`}
              className={`h-8 px-3 rounded-md text-xs font-semibold transition-colors ${period === id ? 'bg-[hsl(var(--primary))] text-white' : 'text-muted-foreground hover:bg-secondary'}`}>{lbl}</button>
          ))}
        </div>
      </div>
      {error && <p className="text-xs text-destructive mt-2" role="alert">{error}</p>}
      {!data && !error && <Skeleton className="h-16 rounded-lg mt-2" />}
      {data && (
        <>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-muted-foreground capitalize">{label} <span className="normal-case">(facturé)</span></p>
              <p className="text-xl font-semibold font-['Space_Grotesk'] leading-tight" data-testid={`${testId}-current`}>{fmt(cur?.ht)} <span className="text-xs font-normal text-muted-foreground">HT</span></p>
              <p className="text-xs text-muted-foreground">{fmt(cur?.ttc)} TTC · {cur?.count || 0} facture{cur?.count > 1 ? 's' : ''}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground capitalize">{prevLabel}</p>
              {hasPrev ? (
                <>
                  <p className="text-xl font-semibold font-['Space_Grotesk'] leading-tight" data-testid={`${testId}-previous`}>{fmt(prevFull?.ht)} <span className="text-xs font-normal text-muted-foreground">HT</span></p>
                  <p className="text-xs text-muted-foreground">{period === 'month' ? 'mois complet' : 'année complète'} · à date égale {fmt(prev?.ht)}</p>
                </>
              ) : <p className="text-sm text-muted-foreground italic mt-1" data-testid={`${testId}-noprev`}>Pas de CA sur cette période l'an dernier</p>}
            </div>
          </div>
          {hasPrev && delta !== null && delta !== undefined && (
            <p className={`mt-2 text-xs font-medium inline-flex items-center gap-1 ${delta >= 0 ? 'text-[hsl(var(--primary))]' : 'text-destructive'}`} data-testid={`${testId}-delta`}>
              {delta >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />} {delta >= 0 ? '+' : ''}{String(delta).replace('.', ',')} % par rapport à la même date l'an dernier
            </p>
          )}
          {period === 'month' && fc && (
            <div className="mt-3 pt-3 border-t border-border/70" data-testid={`${testId}-forecast`}>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> À venir sur le mois (bons d'intervention)</p>
              <div className="mt-1 grid grid-cols-2 gap-3">
                <div>
                  <p className="text-lg font-semibold font-['Space_Grotesk'] leading-tight" data-testid={`${testId}-forecast-upcoming`}>{fmt(fc.upcoming?.ht)} <span className="text-xs font-normal text-muted-foreground">HT</span></p>
                  <p className="text-xs text-muted-foreground">{fc.upcoming?.hours || 0} h planifiées · {fc.upcoming?.lines || 0} créneau{fc.upcoming?.lines > 1 ? 'x' : ''}</p>
                </div>
                <div>
                  <p className="text-lg font-semibold font-['Space_Grotesk'] leading-tight" data-testid={`${testId}-forecast-done`}>{fmt(fc.to_invoice_done?.ht)} <span className="text-xs font-normal text-muted-foreground">HT</span></p>
                  <p className="text-xs text-muted-foreground inline-flex items-center gap-1"><Receipt className="h-3 w-3" /> réalisé, reste à facturer</p>
                </div>
              </div>
              <p className="mt-2 text-sm"><span className="text-muted-foreground">CA projeté du mois :</span> <span className="font-semibold" data-testid={`${testId}-projected`}>{fmt(data.projected_ht)} HT</span> <span className="text-xs text-muted-foreground">({fmt(data.projected_ttc)} TTC)</span></p>
              {fc.unknown_products?.length > 0 && <p className="text-[11px] text-muted-foreground mt-1">Certaines lignes utilisent un service hors catalogue de l'agence et ne sont pas estimées.</p>}
            </div>
          )}
          {period === 'month' && !socid && data.target && (
            <TargetProgress target={data.target} invoicedHt={cur?.ht || 0} forecastHt={fc?.total_ht || 0} month={month} year={year} onSaved={() => setTick((t) => t + 1)} onUnauthorized={onUnauthorized} />
          )}
          <button type="button" onClick={() => setShowChart((v) => !v)} className="mt-3 w-full h-10 rounded-lg border border-dashed border-border text-xs font-medium text-muted-foreground inline-flex items-center justify-center gap-1.5 hover:bg-secondary" data-testid={`${testId}-chart-toggle`}>
            <BarChart3 className="h-4 w-4" /> {showChart ? 'Masquer le graphique mois par mois' : `Graphique mois par mois ${year} / ${year - 1}`} {showChart ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          {showChart && <MonthlyChart socid={socid} onUnauthorized={onUnauthorized} year={year} />}
        </>
      )}
    </div>
  );
}
